import React from 'react';
import { isEqual } from 'lodash';
import { IProcurementCompany } from '../../models/Procurement';
import { useLazyGetContactByIdQuery } from '../../store/Api';
import { useGetProcurementOrganizationSettingsQuery } from '../../store/endpoints/ProcurementEndpoints';

/**
 * Resolves the organization's default shipment contact (from procurement settings) into a
 * ship-to company. Used to suggest where a non-dropship order should ship — i.e. your own
 * receiving location rather than the end customer.
 */
export function useDefaultShipmentContact(): IProcurementCompany | undefined {
    const settings = useGetProcurementOrganizationSettingsQuery();
    const [getContact] = useLazyGetContactByIdQuery();
    const [contact, setContact] = React.useState<IProcurementCompany | undefined>(undefined);

    const contactId = settings.data?.defaultShipmentContact;
    React.useEffect(() => {
        if (!contactId) {
            setContact(undefined);
            return;
        }
        getContact({ contactId, includeCompanyDetails: true }).then((response) => {
            if ('data' in response && response.data) {
                const c = response.data;
                setContact({
                    contactId: c.id,
                    companyName: c.companyDetails?.companyName,
                    firstName: c.firstName,
                    lastName: c.lastName,
                    email: c.emailAddress,
                    phone: c.phoneNumber,
                    address1: c.companyDetails?.address?.address1,
                    address2: c.companyDetails?.address?.address2,
                    address3: c.companyDetails?.address?.address3,
                    city: c.companyDetails?.address?.city,
                    stateTerritory: c.companyDetails?.address?.stateTerritory,
                    postalCodeZip: c.companyDetails?.address?.postalCodeZip,
                    country: c.companyDetails?.address?.country,
                });
            }
        });
    }, [contactId]);

    return contact;
}

/** True when the company has no meaningful values entered. */
export function isEmptyCompany(company?: IProcurementCompany): boolean {
    return !company || !Object.values(company).some(v => v !== undefined && v !== null && v !== '');
}

/** The ship-to shared by every demand, or undefined when they differ / none is set. */
export function commonShipTo(demands: { shipTo?: IProcurementCompany }[]): IProcurementCompany | undefined {
    const first = demands[0]?.shipTo;
    if (isEmptyCompany(first)) return undefined;
    // Deep, key-order-insensitive comparison (a demand with no ship-to differs from the first).
    return demands.every(d => isEqual(d.shipTo, first)) ? first : undefined;
}
